"use client";

import { ICONS_MODULES_PATHS } from "components/Layout/Sidebar/components/body/content/utils/iconModulesPath";
import {
  LiquidPill,
  LiquidPillGroup,
  type LiquidPillGroupItem,
} from "components/ui/v2";
import { CelebrationUserItemV2 } from "components/ui/v2/explore/bundle/CelebrationUserItemV2";
import { ExploreRankingModalV2 } from "components/ui/v2/explore/ExploreRankingModalV2";
import useTranslation from "next-translate/useTranslation";
import { useMemo, useState } from "react";
import { IUser } from "types/User.types";

export interface CelebrationModalV2Props {
  open: boolean;
  onClose: () => void;
  current: IUser[];
  following: IUser[];
  birthdayCard?: boolean;
  anniversaryCard?: boolean;
  newUsersCard?: boolean;
}

type TabKey = "all" | "today" | "upcoming";

function EmptyState({ emoji, message }: { emoji: string; message: string }) {
  return (
    <div className="flex flex-col items-center justify-center py-12 text-center">
      <span className="mb-4 text-4xl">{emoji}</span>
      <p className="text-[9px] leading-snug text-slate-500 dark:text-zinc-400">
        {message}
      </p>
    </div>
  );
}

export function CelebrationModalV2({
  open,
  onClose,
  current,
  following,
  birthdayCard,
  anniversaryCard,
  newUsersCard,
}: CelebrationModalV2Props) {
  const { t } = useTranslation("celebration");
  const [active, setActive] = useState<TabKey>("all");

  const todayList = current ?? [];
  const upcomingList = following ?? [];

  const title = birthdayCard
    ? t("Cumpleañeros")
    : anniversaryCard
      ? t("Aniversarios")
      : t("Nuevos ingresos");

  const tabItems: LiquidPillGroupItem[] = useMemo(
    () => [
      {
        value: "all",
        label: (
          <span className="flex items-center gap-1">
            {t("Todos")}
            <span className="opacity-75">
              {todayList.length + upcomingList.length}
            </span>
          </span>
        ),
        variant: "slate",
      },
      {
        value: "today",
        label: (
          <span className="flex items-center gap-1">
            {t("Hoy")}
            <span className="opacity-75">{todayList.length}</span>
          </span>
        ),
        variant: "emerald",
      },
      {
        value: "upcoming",
        label: (
          <span className="flex items-center gap-1">
            {t("Próximos")}
            <span className="opacity-75">{upcomingList.length}</span>
          </span>
        ),
        variant: "slate",
      },
    ],
    [t, todayList.length, upcomingList.length]
  );

  const filterTabs = (
    <div className="rounded-full border border-slate-200/40 bg-white/40 p-0.5 shadow-[0_1px_2px_rgba(15,23,42,0.02)] backdrop-blur-[2px] dark:border-zinc-800/40 dark:bg-zinc-900/40">
      <LiquidPillGroup
        items={tabItems}
        activeValue={active}
        onChange={(val) => setActive(val as TabKey)}
        size="sm"
      />
    </div>
  );

  const showToday = active === "all" || active === "today";
  const showUpcoming = active === "all" || active === "upcoming";

  const renderRows = (users: IUser[]) =>
    users.map((user) => (
      <CelebrationUserItemV2
        key={`${user.id}_celeb_modal`}
        user={user}
        birthdayCard={birthdayCard}
        anniversaryCard={anniversaryCard}
        newUsersCard={newUsersCard}
      />
    ));

  const isEmpty =
    (showToday ? todayList.length : 0) +
      (showUpcoming ? upcomingList.length : 0) ===
    0;

  return (
    <ExploreRankingModalV2
      open={open}
      onClose={onClose}
      title={title}
      iconSrc={ICONS_MODULES_PATHS.EXPLORE.EXPLORE_MODULE}
      headerRight={filterTabs}
    >
      {isEmpty && <EmptyState emoji="🎉" message={t("Sin celebraciones")} />}

      {showToday && todayList.length > 0 && (
        <div className="mb-6">
          <div className="mb-2">
            <LiquidPill text={t("Hoy")} variant="emerald" size="sm" />
          </div>
          <div className="flex flex-col gap-1">{renderRows(todayList)}</div>
        </div>
      )}

      {showUpcoming && upcomingList.length > 0 && (
        <div>
          <div className="mb-2">
            <LiquidPill text={t("Próximos")} variant="slate" size="sm" />
          </div>
          <div className="flex flex-col gap-1">{renderRows(upcomingList)}</div>
        </div>
      )}
    </ExploreRankingModalV2>
  );
}

export default CelebrationModalV2;
